import React, { useState, useEffect } from 'react';
import api from "../../../services/api";
import { formatDateWithTime } from '../../../utils/dateFormat';
import { useTableState } from '../utils/useTableState';
import ImageModal from './ImageModal';

const VersionsRow = ({ row, isExpanded, colSpan, onClose }) => {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { modal, setModal, actionHandlers } = useTableState();

    useEffect(() => {
        if (isExpanded) {
            const fetchVersions = async () => {
                try {
                    setLoading(true);
                    setError(null);
                    setVersions([]);

                    const result = await api({
                        url: "/alpha_cst_designer/versions",
                        method: "POST",
                        responseType: "json",
                        data: { creative_id: row.creative_id },
                    });

                    if (result.code === 200) {
                        setVersions(result.data?.data.img_urls || []);
                    } else {
                        setError("Failed to fetch versions");
                    }
                } catch (err) {
                    console.error(err);
                    setError("An error occurred while fetching versions.");
                } finally {
                    setLoading(false);
                }
            };

            fetchVersions();
        }
    }, [isExpanded, row.creative_id]);

    const isImage = (url) => {
        return url.match(/\.(jpeg|jpg|gif|png|webp)$/i);
    };

    const isVideo = (url) => {
        return url.match(/\.(mp4|webm|ogg)$/i);
    };

    if (!isExpanded) return null;

    return (
        <tr className="bg-gray-100">
            <td colSpan={colSpan}>
                <div className="relative">
                    <button
                        onClick={onClose}
                        className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-200 transition-colors"
                        aria-label="Close versions"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-gray-600"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </button>
                    <div className="flex gap-4 py-4 scroll-px-80 overflow-x-auto items-center justify-center">
                        {loading && <p className="text-gray-600">Loading versions...</p>}
                        {error && <p className="text-red-500">{error}</p>}
                        {!loading && versions.length === 0 && !error && (
                            <p className="text-gray-400">No versions available.</p>
                        )}
                        {!loading &&
                            versions.map((img, i) => (
                                <React.Fragment key={i}>
                                    <div className="flex flex-col">
                                        {isImage(img.url) ? (
                                            <img
                                                src={img.url}
                                                alt={`Version ${i + 1}`}
                                                onClick={() => {
                                                    setModal({
                                                        open: true,
                                                        type: 'latestDesign',
                                                        image: img.url
                                                    });
                                                }}
                                                className="h-24 object-contain rounded-md border border-gray-300"
                                            />
                                        ) : isVideo(img.url) ? (
                                            <video
                                                width="150"
                                                height="auto"
                                                controls
                                                onClick={() => {
                                                    setModal({
                                                        open: true,
                                                        type: 'latestDesign',
                                                        image: img.url
                                                    });
                                                }}
                                            >
                                                <source src={img.url} type="video/mp4" />
                                                Your browser does not support the video tag.
                                            </video>
                                        ) : (
                                            <div>Unsupported file type</div>
                                        )}
                                        <span className="font-bold">{i === 0 ? "Sample" : `Version ${i}`}</span>
                                        <span>{formatDateWithTime(img.date)}</span>
                                    </div>
                                    {i !== versions.length - 1 && (
                                        <img
                                            src="/svg/designer/arrow-right.svg"
                                            alt="arrow-right"
                                            className="h-6 w-6"
                                        />
                                    )}
                                </React.Fragment>
                            ))}
                    </div>
                    <ImageModal
                        isOpen={modal.open && modal.type === 'latestDesign'}
                        image={modal.image}
                        alt="version preview"
                        onClose={actionHandlers.closeModal}
                    />
                </div>
            </td>
        </tr>
    );
};

export default VersionsRow;
